import { store } from "~/state/store";
import { IRibbonData, TActiveContent } from "./interfaces";
import { IRibbonState } from "~/state/ribbon/interfaces";
import { MAX_HEIGHT_PERC, MIN_WINDOW_HEIGHT, MIN_WINDOW_WIDTH, RIBBON_HEIGHT, PHOTOS_MULTI_LINE_HEIGHT, ENABLE_RIBBON_ROBOTS } from "./const";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { TLoadContentReason } from "./analytics";
import { CONFIG } from "~/config";
import { ELoginState } from "~/state/user/interfaces";
import { IMapState } from "~/state/map/interfaces";

type TStepEventName = "start" | "step" | "end";

interface ILineWidths {
	items: IRibbonData["photos"];
	perLine: number;
	gap: number;
}

function getLineWidths(data: ILineWidths) {
	const imgHeight = PHOTOS_MULTI_LINE_HEIGHT * 2;
	const widths: Array<number> = [];
	const items: Array<{ width: number; height: number; ratio: number; widthRatio: number; }> = [];
	let allWidths = 0;

	// rozmery jednotlivych obrazku
	data.items.forEach(item => {
		const ratio = item.imageWidth / item.imageHeight;
		const width = Math.floor(ratio * imgHeight);
		const height = imgHeight;

		allWidths += width;
		items.push({
			width,
			height,
			ratio,
			widthRatio: 0,
		});
	});

	// podily jednotlivych fotek
	items.forEach(item => {
		const perc = item.width / allWidths;

		widths.push(perc);
	});

	const gap = (Math.max(data.items.length - 1, 0) * data.gap) / data.items.length;
	const output: Array<{ width: number; gap: number; }> = [];

	widths.forEach(width => {
		output.push({
			width,
			gap,
		});
	});

	return output;
}

export function calculateWidths(data: ILineWidths) {
	const firstLine = data.items.slice(0, data.perLine);
	const secondLine = data.items.slice(data.perLine, data.items.length);

	return [
		...getLineWidths({ ...data, items: firstLine }),
		...getLineWidths({ ...data, items: secondLine }),
	];
}


export function getStyleValues(elem: HTMLElement, attrs: Array<keyof CSSStyleDeclaration>, defaultValue = 0) {
	const style = getComputedStyle(elem);
	const output: Array<number> = [];

	attrs.forEach(attr => {
		let attrValue = defaultValue;
		const value = (style[attr] || "") as string;

		if (value.indexOf("px") !== -1) {
			attrValue = parseFloat(value.replace("px", ""));
		}

		output.push(attrValue);
	});

	return output;
}

export function onStep(duration: number, steps: number, cbOnStep: (name: TStepEventName, step: number) => void) {
	const stepInterval = duration / steps;
	let curStep = 1;

	cbOnStep("start", 1);

	let id = setInterval(() => {
		curStep++;

		if (curStep === steps) {
			cbOnStep("end", steps);
			clearInterval(id);
			id = null;
		} else {
			cbOnStep("step", curStep);
		}
	}, stepInterval);

	return () => {
		if (id) {
			clearInterval(id);
			cbOnStep("end", steps);
		}
	};
}

/* init ribbonu */
function getResolutionTest() {
	const widthTest = window.innerWidth >= MIN_WINDOW_WIDTH;
	const heightTest = window.innerHeight >= MIN_WINDOW_HEIGHT;
	const maxHeightTest = window.innerHeight > RIBBON_HEIGHT
		? RIBBON_HEIGHT / window.innerHeight <= MAX_HEIGHT_PERC / 100
		: false;

	return widthTest && heightTest && maxHeightTest;
}

export function getShowReason(): TLoadContentReason {
	const isMobile = Mapy.Util.isMobile();
	const isLowResolution = !getResolutionTest();
	const isIframe = CONFIG.inFrame;

	if (isMobile) {
		return "responsive";
	} else if (isLowResolution) {
		return "low_resolution";
	} else if (isIframe) {
		return "iframe";
	}

	return "none";
}

export function getRibbonRobotsTest() {
	if (!CONFIG.isScreenshoter || !ENABLE_RIBBON_ROBOTS) {
		return true;
	}

	const params = Array.from(new URLSearchParams(location.search).entries());
	const keys = params.map(param => param[0]);
	const hasSourceCoor = params.filter(param => param[0] === "source" && param[1] === "coor");

	return params.length === 2 && keys.includes("source") && keys.includes("id") && !hasSourceCoor;
}

function getRibbonTests() {
	const iframeTest = !CONFIG.inFrame;
	const screenshoterTest = getRibbonRobotsTest();
	const langTest = CONFIG.defaultLang === "cs";
	const resolutionTest = getResolutionTest();
	const mobileTest = Mapy.Util.isMobile();
	const allTests = resolutionTest && iframeTest && screenshoterTest && langTest && !mobileTest;

	return allTests;
}

export function getMinimalizedVariant() {
	const state = store.getState();

	return state.user.user.state === ELoginState.Green && state.ribbon.userVariant === "B";
}

/* ziskani viditelnosti ribbonu */
function isFullWidth() {
	const layout = Mapy.getComponent("layout");

	return layout.getWidth() === 1;
}

function isPanoOverlayActive(mapState: IMapState) {
	return mapState.overlays.includes(MAP_OVERLAYS.PANORAMA);
}

function getDetailActive(ribbonState: IRibbonState) {
	const curContent: TActiveContent = ribbonState.activeContents.length > 0
		? ribbonState.activeContents[ribbonState.activeContents.length - 1]
		: "search";

	return curContent === "detail";
}

export function getRibbonData() {
	const state = store.getState();
	const usp = new URLSearchParams(location.search);
	const loadDetailFromUrl = usp.get("source") !== null && usp.get("id") !== null;
	const isDetailActive = getDetailActive(state.ribbon);
	const isPanoActive = isPanoOverlayActive(state.map);
	const othersTest = isFullWidth() && !isPanoActive;
	const ribbonTests = getRibbonTests();
	const canBeShown = isDetailActive && othersTest && ribbonTests;

	return {
		ribbonState: state.ribbon,
		detailState: state.detail,
		canBeShown,
		othersTest,
		isDetailActive,
		isPanoActive,
		loadDetailFromUrl,
		ribbonTests,
	};
}
